/**
 * Retry utility with exponential backoff for upload operations
 * Used to handle transient network failures in recording uploads
 */

export interface RetryOptions {
  maxAttempts: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
  jitterPercent: number;
  shouldRetry?: (error: Error, attempt: number) => boolean;
  onRetry?: (attempt: number, error: Error, nextDelayMs: number) => void;
}

export interface RetryResult<T> {
  success: boolean;
  data?: T;
  error?: Error;
  attempts: number;
  totalTimeMs: number;
}

const defaultOptions: RetryOptions = {
  maxAttempts: 3,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  backoffMultiplier: 2,
  jitterPercent: 0.1,
  shouldRetry: (error: Error) => {
    // Retry on network errors and 5xx server errors
    const message = error.message.toLowerCase();
    const isNetworkError =
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('connection') ||
      message.includes('econnrefused') ||
      message.includes('fetch');

    const isServerError =
      message.includes('500') ||
      message.includes('502') ||
      message.includes('503') ||
      message.includes('504');

    // Don't retry on client errors (4xx)
    const isClientError =
      message.includes('400') ||
      message.includes('401') ||
      message.includes('403') ||
      message.includes('404') ||
      message.includes('409') ||
      message.includes('checksum');

    return (isNetworkError || isServerError) && !isClientError;
  }
};

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(
  attempt: number,
  initialDelayMs: number,
  maxDelayMs: number,
  backoffMultiplier: number,
  jitterPercent: number
): number {
  // Exponential backoff: initialDelay * multiplier^(attempt-1)
  const exponentialDelay = initialDelayMs * Math.pow(backoffMultiplier, attempt - 1);

  // Cap at max delay
  const cappedDelay = Math.min(exponentialDelay, maxDelayMs);

  // Add jitter to prevent thundering herd
  const jitter = cappedDelay * jitterPercent * (Math.random() * 2 - 1);

  return Math.max(0, cappedDelay + jitter);
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Execute a function with automatic retry on failure
 * Uses exponential backoff with jitter
 *
 * @example
 * const result = await withRetry(
 *   () => uploadRecording(data),
 *   {
 *     maxAttempts: 3,
 *     onRetry: (attempt, error) => console.log(`Retry ${attempt}: ${error.message}`)
 *   }
 * );
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<RetryResult<T>> {
  const opts = { ...defaultOptions, ...options };
  const startTime = Date.now();
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    try {
      const data = await fn();
      return {
        success: true,
        data,
        attempts: attempt,
        totalTimeMs: Date.now() - startTime
      };
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Check if we should retry
      const shouldRetry = opts.shouldRetry?.(lastError, attempt) ?? true;

      if (!shouldRetry || attempt === opts.maxAttempts) {
        // Don't retry - either shouldn't or last attempt
        break;
      }

      // Calculate delay for next attempt
      const delayMs = calculateDelay(
        attempt,
        opts.initialDelayMs,
        opts.maxDelayMs,
        opts.backoffMultiplier,
        opts.jitterPercent
      );

      // Notify about retry
      opts.onRetry?.(attempt, lastError, delayMs);

      // Wait before retrying
      await sleep(delayMs);
    }
  }

  return {
    success: false,
    error: lastError,
    attempts: opts.maxAttempts,
    totalTimeMs: Date.now() - startTime
  };
}

/**
 * Predefined retry configurations for common scenarios
 */
export const RetryConfigs = {
  /** Fast retry for quick operations (3 attempts, 1s initial delay) */
  fast: {
    maxAttempts: 3,
    initialDelayMs: 1000,
    maxDelayMs: 10000,
    backoffMultiplier: 2,
    jitterPercent: 0.1
  } as Partial<RetryOptions>,

  /** Standard retry for uploads (3 attempts, 2s initial delay) */
  upload: {
    maxAttempts: 3,
    initialDelayMs: 2000,
    maxDelayMs: 30000,
    backoffMultiplier: 2,
    jitterPercent: 0.15
  } as Partial<RetryOptions>,

  /** Aggressive retry for critical operations (5 attempts, 1s initial delay) */
  critical: {
    maxAttempts: 5,
    initialDelayMs: 1000,
    maxDelayMs: 60000,
    backoffMultiplier: 2,
    jitterPercent: 0.1
  } as Partial<RetryOptions>,

  /** Background retry with longer delays (5 attempts, 5min to 24hr) */
  background: {
    maxAttempts: 5,
    initialDelayMs: 5 * 60 * 1000, // 5 minutes
    maxDelayMs: 24 * 60 * 60 * 1000, // 24 hours
    backoffMultiplier: 3,
    jitterPercent: 0.2
  } as Partial<RetryOptions>
};

/**
 * Error codes for upload failures
 */
export enum UploadErrorCode {
  NETWORK_ERROR = 'UPLOAD_NETWORK_ERROR',
  TIMEOUT = 'UPLOAD_TIMEOUT',
  CANCELLED = 'UPLOAD_CANCELLED',
  CHECKSUM_MISMATCH = 'CHECKSUM_MISMATCH',
  BLOB_WRITE_FAILED = 'BLOB_WRITE_FAILED',
  SERVER_ERROR = 'SERVER_ERROR',
  SUBSCRIPTION_REQUIRED = 'SUBSCRIPTION_REQUIRED',
  RECORDING_NOT_ALLOWED = 'RECORDING_NOT_ALLOWED',
  VISIT_NOT_ACTIVE = 'VISIT_NOT_ACTIVE',
  VISIT_ALREADY_RECORDED = 'VISIT_ALREADY_RECORDED',
  UNKNOWN = 'UNKNOWN_ERROR'
}

/**
 * Classify an error into an upload error code
 */
/**
 * The server refused the recording because of the user's role (AMP-136: in a standalone practice
 * org admins do not record). Final: retrying cannot succeed.
 */
export function isRecordingNotAllowed(error: unknown): boolean {
  const e = error as { code?: string; message?: string; response?: { status?: number; data?: { code?: string } } } | null;
  if (!e) return false;
  if (e.response?.data?.code === 'RECORDING_ROLE_REQUIRED' || e.code === 'RECORDING_ROLE_REQUIRED') return true;
  const message = (e.message || '').toLowerCase();
  return message.includes('recording_role_required') || message.includes('recordings are made by providers');
}

/** Shown when the server refuses audio for a cancelled or no-show visit. */
export const VISIT_NOT_ACTIVE_MESSAGE =
  'This visit was cancelled or marked as a no-show; put it back on the schedule to record it';

/**
 * The server refused the recording because the visit was cancelled or marked as a no-show
 * (409 VISIT_NOT_ACTIVE). Final until someone puts the visit back on the schedule.
 */
export function isVisitNotActive(error: unknown): boolean {
  const e = error as { code?: string; message?: string; response?: { data?: { code?: string } } } | null;
  if (!e) return false;
  if (e.response?.data?.code === 'VISIT_NOT_ACTIVE' || e.code === 'VISIT_NOT_ACTIVE') return true;
  const message = (e.message || '').toLowerCase();
  return message.includes('visit_not_active') || message.includes('put it back on the schedule to record it');
}

/** Shown when the server refuses audio for a visit that already has a recording. */
export const VISIT_ALREADY_RECORDED_MESSAGE = 'This visit already has a recording. Open its note to review it';

/**
 * The server refused the recording because another recording of the visit already holds its
 * audio (409 VISIT_ALREADY_RECORDED). Final: the visit is recorded.
 */
export function isVisitAlreadyRecorded(error: unknown): boolean {
  const e = error as { code?: string; message?: string; response?: { data?: { code?: string } } } | null;
  if (!e) return false;
  if (e.response?.data?.code === 'VISIT_ALREADY_RECORDED' || e.code === 'VISIT_ALREADY_RECORDED') return true;
  const message = (e.message || '').toLowerCase();
  return message.includes('visit_already_recorded') || message.includes('this visit already has a recording');
}

/**
 * A refusal that retrying cannot fix (the user's role, a cancelled or no-show visit, or a visit
 * that already has a recording). The audio is kept on the device as needs_attention and left out
 * of automatic retries.
 */
export function isFinalUploadRefusal(error: unknown): boolean {
  return isRecordingNotAllowed(error) || isVisitNotActive(error) || isVisitAlreadyRecorded(error);
}

export function classifyUploadError(error: Error): UploadErrorCode {
  const message = error.message.toLowerCase();

  if (isVisitNotActive(error)) {
    return UploadErrorCode.VISIT_NOT_ACTIVE;
  }
  if (isVisitAlreadyRecorded(error)) {
    return UploadErrorCode.VISIT_ALREADY_RECORDED;
  }
  if (isRecordingNotAllowed(error)) {
    return UploadErrorCode.RECORDING_NOT_ALLOWED;
  }

  // Check for subscription-related errors (HTTP 402)
  if (message.includes('subscription') || message.includes('402') || message.includes('payment required')) {
    return UploadErrorCode.SUBSCRIPTION_REQUIRED;
  }
  if (message.includes('timeout')) {
    return UploadErrorCode.TIMEOUT;
  }
  if (message.includes('abort') || message.includes('cancel')) {
    return UploadErrorCode.CANCELLED;
  }
  if (message.includes('checksum')) {
    return UploadErrorCode.CHECKSUM_MISMATCH;
  }
  if (message.includes('blob')) {
    return UploadErrorCode.BLOB_WRITE_FAILED;
  }
  if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
    return UploadErrorCode.NETWORK_ERROR;
  }
  if (message.includes('500') || message.includes('502') || message.includes('503')) {
    return UploadErrorCode.SERVER_ERROR;
  }

  return UploadErrorCode.UNKNOWN;
}

/**
 * Check if an error code is retryable
 */
export function isRetryableError(code: UploadErrorCode): boolean {
  const retryableCodes = [
    UploadErrorCode.NETWORK_ERROR,
    UploadErrorCode.TIMEOUT,
    UploadErrorCode.SERVER_ERROR,
    UploadErrorCode.BLOB_WRITE_FAILED
  ];

  return retryableCodes.includes(code);
}
